// context/AuthContext.js
'use client';

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export type AuthContextType = {
  loggedIn: boolean;
  user: any | null;
  /**
   * Register demand for Firebase Auth. Idempotent — the first call dynamically
   * imports the Firebase SDK and starts the onAuthStateChanged subscription;
   * later calls are no-ops. Consumers that need login state should not call this
   * directly — use the `useAuth()` hook, which registers demand on mount.
   */
  ensureAuthInitialized: () => void;
};

export const AuthContext = createContext<AuthContextType>({
  loggedIn: false,
  user: null,
  ensureAuthInitialized: () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [user, setUser] = useState<any>(null);
  const initializedRef = useRef(false);

  // Demand-based initialization. We deliberately do NOT init on idle/timer/
  // interaction: the app is Supabase-first and nothing on public product/search
  // pages consumes auth (the profile button is dev-only and price alerts are
  // disabled). Loading the Firebase SDK + its auth-iframe/gapi/identitytoolkit
  // third-party chain there would be pure waste. Instead, only a real consumer
  // (login button, profile button, sign-up/admin flows — all on articles/dev
  // routes) triggers the load, via useAuth(). On those routes login state still
  // resolves on mount with no user interaction required.
  const ensureAuthInitialized = useCallback(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    void (async () => {
      const [{ getAuthInstance }, { onAuthStateChanged }] = await Promise.all([
        import('@/lib/firebase'),
        import('firebase/auth'),
      ]);
      onAuthStateChanged(getAuthInstance(), (u) => {
        if (u) {
          setLoggedIn(true);
          setUser(u);
        } else {
          setLoggedIn(false);
          setUser(null);
        }
      });
    })();
  }, []);

  return (
    <AuthContext.Provider value={{ loggedIn, user, ensureAuthInitialized }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Read auth state AND register demand for it. Any component that needs to know
 * whether the user is signed in should use this (not a bare useContext) so the
 * Firebase SDK loads on mount and state resolves without user interaction.
 *
 * Passive readers that merely want to react to an already-loaded session
 * WITHOUT causing Firebase to load (e.g. the global credit-limit banner) should
 * use `useContext(AuthContext)` directly instead.
 */
export function useAuth(): { loggedIn: boolean; user: any | null } {
  const { loggedIn, user, ensureAuthInitialized } = useContext(AuthContext);
  useEffect(() => {
    ensureAuthInitialized();
  }, [ensureAuthInitialized]);
  return { loggedIn, user };
}
